import React, { useState, useEffect } from 'react';
import { ArrowUp, Paperclip, Globe, Lock, Loader2, Check, Hammer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { heroSuggestions } from '../../mock';
import { useAuth } from '../../lib/AuthContext';
import { supabase, PENDING_PROMPT_KEY } from '../../lib/supabase';
import GuidedIntake from './GuidedIntake';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Stash for the intake payload when a logged-out visitor submits the wizard —
// we redirect them through signup, then complete project+generation after
// auth returns them here.
const PENDING_INTAKE_KEY = 'yb.pending_intake_v1';

const deriveName = (p) => {
  const cleaned = (p || '').replace(/\s+/g, ' ').trim();
  return cleaned.length > 60 ? cleaned.slice(0, 57) + '\u2026' : cleaned || 'Untitled project';
};

// Server-side plan gate: ask the backend whether the caller can create
// another saved project right now (Free = 3, Pro/Enterprise = unlimited).
// Returns `null` when the check succeeds, or a user-facing message when
// blocked. Failing OPEN on network errors is fine — the /generate*
// endpoints re-enforce the cap server-side, so a UI bypass here still
// can't populate a 4th project.
const preflightNewProject = async () => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) return null;
    const { data } = await axios.get(`${API}/projects-preflight`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 8000,
    });
    if (data && data.allowed === false) {
      return data.message || 'You’ve hit your saved-project limit.';
    }
    return null;
  } catch (_) {
    return null;
  }
};

const STARTER_PROMPTS = [
  'a website for my plumbing business in Brisbane',
  'an electrician site with a quote form',
  'a lawn care business landing page',
  'a cleaning business site in Melbourne',
  'a mobile car detailing landing page',
];

// One-click starter for the flagship tradie template. This produces a rich
// prompt so the LLM builds the full trade site structure while OBEYING the
// generation Hard Rules: never fabricate testimonials, ABN, licences, years in
// business, awards, ratings, or statistics — anything not supplied is either
// omitted or shown as a highlighted [Add your …] placeholder for the owner to fill.
export const TRADIE_TEMPLATE_PROMPT = [
  'Build a professional website for an Australian tradie business.',
  'Pick a specific trade (plumber, electrician, HVAC, cleaner, detailer, handyman, or landscaper) and a specific Australian city if none is specified.',
  'Structure (fixed order): hero with the business name, trade, one-line tagline, service area, a large click-to-call button (tel: link), and a "Get a quote" button that scrolls to the form. Apply a dark bottom-up gradient overlay on the hero photo, not a flat colour wash, so text stays crisp.',
  'A trust row with 3 to 4 items built ONLY from supplied facts (Licensed & insured, Local team, etc.). Do NOT invent years in business, licence numbers, ABN, awards, or ratings — show [Add your ABN] / [Add your licence] style placeholders instead.',
  'A services list with 5 to 7 services relevant to the trade, each with a simple inline SVG icon.',
  'Testimonials section ONLY if real testimonials were supplied. If none were supplied, show a single "Add your Google reviews" placeholder card. Never invent customers, names, star ratings, or review counts.',
  'A service area section listing supplied suburbs as pill chips.',
  'A quote request form with name, phone, suburb, and message fields (wired to capture leads automatically).',
  'A sticky "Call now" bar fixed to the bottom on mobile only.',
  'A footer with business name, phone, hours, ABN (flagged if missing), service area, and socials (only if links supplied).',
  'Never output empty containers, blank bars, search boxes, or nav links to pages that do not exist.',
  'Australian framing throughout: tradie, ABN, real Australian suburb names, AUD.',
].join(' ');

const Hero = () => {
  const [value, setValue] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [display, setDisplay] = useState('');
  const [typing, setTyping] = useState(true);
  const [buildStatus, setBuildStatus] = useState('idle'); // idle | loading | saved | error
  const [buildMsg, setBuildMsg] = useState('');
  // Guided intake is the default entry; Advanced toggles the classic textarea.
  const [mode, setMode] = useState('guided'); // 'guided' | 'advanced'
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (value) return;
    const target = heroSuggestions[phraseIdx];
    let i = 0;
    let timer;
    if (typing) {
      timer = setInterval(() => {
        i++;
        setDisplay(target.slice(0, i));
        if (i >= target.length) {
          setTyping(false);
          clearInterval(timer);
          setTimeout(() => setTyping('erase'), 1600);
        }
      }, 40);
    } else if (typing === 'erase') {
      timer = setInterval(() => {
        i++;
        setDisplay(target.slice(0, Math.max(0, target.length - i)));
        if (i >= target.length) {
          clearInterval(timer);
          setPhraseIdx((p) => (p + 1) % heroSuggestions.length);
          setTyping(true);
        }
      }, 20);
    }
    return () => clearInterval(timer);
  }, [phraseIdx, typing, value]);

  const handleBuild = async () => {
    const prompt = value.trim();
    if (!prompt) {
      setBuildStatus('error');
      setBuildMsg('Type your idea first, then hit Build.');
      return;
    }
    if (!user) {
      sessionStorage.setItem(PENDING_PROMPT_KEY, prompt);
      navigate('/signup?next=/projects');
      return;
    }
    setBuildStatus('loading');
    setBuildMsg('');
    // Server-side plan gate before we insert a new row.
    const capMsg = await preflightNewProject();
    if (capMsg) {
      setBuildStatus('error');
      setBuildMsg(capMsg);
      return;
    }
    const { data: inserted, error } = await supabase
      .from('user_projects')
      .insert({ user_id: user.id, name: deriveName(prompt), prompt })
      .select('id')
      .single();
    if (error) {
      setBuildStatus('error');
      setBuildMsg(error.message || 'Could not save your project.');
      return;
    }
    setBuildStatus('saved');
    setBuildMsg('Saved. Building your website…');
    setValue('');
    setTimeout(() => navigate(`/projects/${inserted.id}`, { state: { autoGenerate: true } }), 500);
  };

  const useTradieTemplate = () => {
    setMode('advanced');
    setValue(TRADIE_TEMPLATE_PROMPT);
    setBuildStatus('idle');
    setBuildMsg('');
  };

  const deriveNameFromIntake = (intake) => {
    const trade = intake?.trade === 'other'
      ? (intake?.trade_other || 'Trade')
      : (intake?.trade || 'Trade');
    const town = (intake?.town || '').trim();
    const biz = (intake?.business_name || '').trim();
    if (biz) return biz.length > 60 ? biz.slice(0, 57) + '…' : biz;
    const base = `${trade[0].toUpperCase()}${trade.slice(1)} in ${town || 'Australia'}`;
    return base.length > 60 ? base.slice(0, 57) + '…' : base;
  };

  const runGuidedGenerate = async (projectId, session, intake) => {
    const token = session?.access_token;
    if (!token) throw new Error('Not signed in.');
    await axios.post(
      `${API}/projects/${projectId}/generate-guided`,
      intake,
      { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
    );
  };

  const handleGuidedSubmit = async (intake) => {
    setBuildStatus('loading');
    setBuildMsg('');
    // Logged-out visitors: stash the intake, send them through signup, then
    // we'll resume from the useEffect below.
    if (!user) {
      try { sessionStorage.setItem(PENDING_INTAKE_KEY, JSON.stringify(intake)); } catch (_) {}
      navigate('/signup?next=/projects');
      return;
    }
    const projectName = deriveNameFromIntake(intake);
    // Same server-side plan gate — reject before we create a stale row.
    const capMsg = await preflightNewProject();
    if (capMsg) {
      setBuildStatus('error');
      setBuildMsg(capMsg);
      return;
    }
    const { data: inserted, error } = await supabase
      .from('user_projects')
      .insert({ user_id: user.id, name: projectName, prompt: '' })
      .select('id')
      .single();
    if (error) {
      setBuildStatus('error');
      setBuildMsg(error.message || 'Could not save your project.');
      return;
    }
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      await runGuidedGenerate(inserted.id, sessionData?.session, intake);
    } catch (err) {
      // Even if the generation kickoff fails, the empty project exists —
      // navigate to it so the user can retry from there.
      const msg = err?.response?.data?.detail || err?.message || 'Could not start generation.';
      setBuildStatus('error');
      setBuildMsg(typeof msg === 'string' ? msg : 'Could not start generation.');
      setTimeout(() => navigate(`/projects/${inserted.id}`), 700);
      return;
    }
    setBuildStatus('saved');
    setBuildMsg('Saved. Building your website…');
    setTimeout(() => navigate(`/projects/${inserted.id}`), 400);
  };

  // Resume a pending intake after a logged-out user completes signup.
  useEffect(() => {
    if (!user) return;
    let pending = null;
    try { pending = sessionStorage.getItem(PENDING_INTAKE_KEY); } catch (_) {}
    if (!pending) return;
    let intake;
    try { intake = JSON.parse(pending); } catch (_) { intake = null; }
    try { sessionStorage.removeItem(PENDING_INTAKE_KEY); } catch (_) {}
    if (intake && intake.trade && intake.town) {
      handleGuidedSubmit(intake);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" style={{ backgroundColor: 'var(--yb-bg)' }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-24 -left-24 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(79,70,229,0.16), transparent 70%)' }} />
        <div className="absolute top-40 -right-24 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle at 70% 30%, rgba(245,158,11,0.14), transparent 70%)' }} />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white/70 backdrop-blur-sm text-[12.5px] mb-8"
          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
          data-testid="hero-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--yb-indigo)' }} />
          Built for Aussie tradies: plumbers, sparkies, cleaners, landscapers.
        </div>
        <h1
          className="font-serif text-[44px] sm:text-[64px] lg:text-[88px] leading-[1] tracking-[-0.03em]"
          style={{ color: 'var(--yb-ink)' }}
          data-testid="hero-headline"
        >
          Your trade business deserves
          <br />
          <span className="italic">a website that brings in jobs.</span>
        </h1>
        <p
          className="mt-6 text-[17px] sm:text-[19px] max-w-2xl mx-auto"
          style={{ color: 'var(--yb-muted)' }}
          data-testid="hero-subhead"
        >
          Describe your business in plain English, and YouBuild builds a clean, mobile website with click to call and a quote form, live in minutes. No coding, no agency, no waiting.
        </p>

        <div className="mt-10 max-w-[720px] mx-auto">
          {mode === 'guided' ? (
            <GuidedIntake
              busy={buildStatus === 'loading' || buildStatus === 'saved'}
              submitLabel="Build my website"
              onSubmit={handleGuidedSubmit}
              onAdvanced={() => { setMode('advanced'); setBuildStatus('idle'); setBuildMsg(''); }}
            />
          ) : (
          <div className="relative bg-white rounded-2xl border p-4 pt-5 shadow-[0_10px_40px_-15px_rgba(23,21,59,0.2)]" style={{ borderColor: 'var(--yb-border)' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                <Hammer size={14} style={{ color: 'var(--yb-amber)' }} />
                <span>Advanced — write your own prompt</span>
              </div>
              <button
                type="button"
                onClick={() => { setMode('guided'); setBuildStatus('idle'); setBuildMsg(''); }}
                data-testid="hero-back-to-guided"
                className="text-[12.5px] underline decoration-dotted underline-offset-4"
                style={{ color: 'var(--yb-indigo)' }}
              >
                ← Back to guided setup
              </button>
            </div>
            <div className="relative">
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder=""
                rows={2}
                aria-label="Describe your trade business"
                data-testid="hero-prompt-textarea"
                className="w-full resize-none bg-transparent text-[16px] outline-none px-2"
                style={{ color: 'var(--yb-ink)' }}
              />
              {!value && (
                <div className="pointer-events-none absolute top-0 left-2 text-[16px] text-left" style={{ color: '#9CA3AF' }}>
                  Ask YouBuild to create {display}
                  <span className="inline-block w-[2px] h-[18px] ml-0.5 align-middle animate-pulse" style={{ backgroundColor: '#9CA3AF' }} />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'var(--yb-border)' }}>
              <div className="flex items-center gap-1">
                <button className="p-2 rounded-lg hover:bg-[#F5F4F0] transition-colors" style={{ color: 'var(--yb-muted)' }} aria-label="Attach">
                  <Paperclip size={17} />
                </button>
                <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-[#F5F4F0] text-[13px] transition-colors" style={{ color: 'var(--yb-muted)' }}>
                  <Globe size={14} /> Public
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-[#F5F4F0] text-[13px] transition-colors" style={{ color: 'var(--yb-muted)' }}>
                  <Lock size={13} /> Private
                </button>
                <button
                  onClick={handleBuild}
                  disabled={buildStatus === 'loading' || buildStatus === 'saved'}
                  data-testid="hero-build-btn"
                  className="yb-btn-primary flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold disabled:opacity-70"
                >
                  {buildStatus === 'loading' && <Loader2 size={14} className="animate-spin" />}
                  {buildStatus === 'saved' && <Check size={14} />}
                  {(buildStatus === 'idle' || buildStatus === 'error') && (<>Build my website <ArrowUp size={15} /></>)}
                  {buildStatus === 'loading' && 'Saving…'}
                  {buildStatus === 'saved' && 'Saved'}
                </button>
              </div>
            </div>
          </div>
          )}

          {mode === 'advanced' && (
          <>
          {/* Flagship tradie template starter */}
          <button
            type="button"
            onClick={useTradieTemplate}
            data-testid="tradie-template-btn"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border bg-white text-[13.5px] font-medium transition-colors hover:border-[var(--yb-indigo)] hover:text-[var(--yb-indigo)]"
            style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
          >
            <Hammer size={15} style={{ color: 'var(--yb-amber)' }} />
            Use the tradie template
            <span className="text-[12px]" style={{ color: 'var(--yb-muted)' }}>(hero, services, reviews, quote form)</span>
          </button>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <span className="text-[12.5px] mr-1" style={{ color: 'var(--yb-muted)' }}>Try:</span>
            {STARTER_PROMPTS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setValue(s); setBuildStatus('idle'); setBuildMsg(''); }}
                data-testid={`hero-starter-${s.slice(0, 20).replace(/\s+/g, '-')}`}
                className="px-3 py-1.5 rounded-full border bg-white text-[12.5px] transition-colors hover:border-[var(--yb-indigo)] hover:text-[var(--yb-indigo)]"
                style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
              >
                {s}
              </button>
            ))}
          </div>
          </>
          )}

          {buildStatus === 'error' && (
            <p className="mt-3 text-[13px]" style={{ color: '#B4530A' }} data-testid="hero-build-error">{buildMsg}</p>
          )}
          {buildStatus === 'saved' && (
            <p className="mt-3 text-[13px]" style={{ color: 'var(--yb-indigo)' }} data-testid="hero-build-saved">{buildMsg} Taking you to your project…</p>
          )}
          {buildStatus === 'idle' && !user && (
            <p className="mt-3 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
              You'll create a free account to save your website. It takes 10 seconds.
            </p>
          )}
          {buildStatus === 'idle' && user && (
            <p className="mt-3 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
              Logged in as <span style={{ color: 'var(--yb-ink)' }}>{user.email}</span>. Your work saves to <span style={{ color: 'var(--yb-indigo)' }}>My projects</span>.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
